export const FRAMEBUFFER_WIDTH = 320;
export const FRAMEBUFFER_HEIGHT = 200;
export const FRAMEBUFFER_SIZE = FRAMEBUFFER_WIDTH * FRAMEBUFFER_HEIGHT * 4;
export const MEMORY_SIZE = 0x10000;
export const STORAGE_UNIT_COUNT = 2;
export const TOOLING_MODE_NAMES = [
  "imp",
  "acc",
  "imm",
  "zp",
  "abs",
  "ind",
  "zpx",
  "zpy",
  "absx",
  "absy",
  "rel",
  "indx",
  "indy",
  "indy_pre",
  "indx_post"
] as const;
export const WORKBENCH_DIRECTIVE_KIND = {
  byte: 0,
  word: 1,
  fill: 2,
  res: 3,
  org: 4,
  align: 5
} as const;

export const ROM_KIND = {
  system: 0,
  monitor: 1,
  char: 2
} as const;

export const AM8_KEY = {
  none: 0,
  runstop: 1,
  digit1: 2,
  digit2: 3,
  digit3: 4,
  digit4: 5,
  digit5: 6,
  digit6: 7,
  digit7: 8,
  digit8: 9,
  digit9: 10,
  digit0: 11,
  ssQuestion: 12,
  acuteGrave: 13,
  backspace: 14,
  q: 15,
  w: 16,
  e: 17,
  r: 18,
  t: 19,
  z: 20,
  u: 21,
  i: 22,
  o: 23,
  p: 24,
  ue: 25,
  plusAsterisk: 26,
  a: 27,
  s: 28,
  d: 29,
  f: 30,
  g: 31,
  h: 32,
  j: 33,
  k: 34,
  l: 35,
  oe: 36,
  ae: 37,
  hashApostrophe: 38,
  enter: 39,
  shiftLeft: 40,
  ltGtPipe: 41,
  y: 42,
  x: 43,
  c: 44,
  v: 45,
  b: 46,
  n: 47,
  m: 48,
  commaSemicolon: 49,
  periodColon: 50,
  minusUnderscore: 51,
  shiftRight: 52,
  ctrl: 53,
  alt: 54,
  space: 55,
  left: 56,
  right: 57,
  up: 58,
  down: 59,
  homeClr: 60,
  f1: 61,
  f3: 62,
  f5: 63,
  f7: 64
} as const;

export type RomKind = (typeof ROM_KIND)[keyof typeof ROM_KIND];
export type Am8Key = (typeof AM8_KEY)[keyof typeof AM8_KEY];
export type StorageUnit = 0 | 1;
export type ToolingModeName = (typeof TOOLING_MODE_NAMES)[number];
export type WorkbenchDirectiveKind = (typeof WORKBENCH_DIRECTIVE_KIND)[keyof typeof WORKBENCH_DIRECTIVE_KIND];

type ToolingModeDetection = {
  mode: ToolingModeName;
  preferAbs: boolean;
  value: number | null;
};

export type DirectiveEvalResult = {
  bytes: Uint8Array;
  nextPc: number;
};

export type CoreSnapshot = {
  pc: number;
  instrPc: number;
  a: number;
  x: number;
  y: number;
  sp: number;
  p: number;
  frameNo: number;
  cycles: string;
  halted: boolean;
  instrBoundary: boolean;
  phase: DebugPhaseName;
  irqLine: boolean;
  nmiLine: boolean;
};

export type VocSnapshot = {
  ctrl1: number;
  raster: number;
  ctrl2: number;
  memPtr: number;
  irqIfr: number;
  irqIer: number;
  spriteXMsb: number;
  spriteEn: number;
  spriteYExpand: number;
  spritePriority: number;
  spriteMulticolor: number;
  spriteXExpand: number;
  spriteColl: number;
  spriteCollBg: number;
  spriteMc0: number;
  spriteMc1: number;
  videoCtrl: number;
  spriteX: Uint8Array;
  spriteY: Uint8Array;
  spriteColor: Uint8Array;
  spritePtr: Uint8Array;
  palette: Uint16Array;
  rasterIrqCompare: number;
  rasterLine: number;
  rasterCycle: number;
  frameNo: number;
  badlineActive: boolean;
  badlineRow: number;
  phiHighOwner: number;
  phiHighReason: number;
  lookaheadCycles: number;
  nextHighFetchDistance: number;
  baLow: boolean;
  aecLow: boolean;
  gfxLineValid: boolean;
  gfxLineVisibleRow: number;
  borderColor: number;
  bgColor: number;
  phiLowVideoCount: string;
  phiHighCpuCount: string;
  phiHighVideoCount: string;
  badlineCount: string;
  spriteDmaCount: string;
  slotFetchCount: string;
  stallCount: string;
};

export type IocSnapshot = {
  kbdData: number;
  kbdStatus: number;
  pra: number;
  prb: number;
  ddra: number;
  ddrb: number;
  joy1: number;
  joy2: number;
  mouseDx: number;
  mouseDy: number;
  mouseBtn: number;
  videoCtrl: number;
  icr: number;
  ctrl: number;
  idcap: number;
  ier: number;
  ifr: number;
  keyMatrix: Uint8Array;
  fifoHead: number;
  fifoTail: number;
  fifoCount: number;
  stickyFifoPushCycleCount: string;
  stickyFifoPopCycleCount: string;
  stickyMatrixCycleCount: string;
  stickyFifoPushFrameNo: number;
  stickyFifoPopFrameNo: number;
  stickyMatrixFrameNo: number;
  stickyFifoCountPeak: number;
  stickyFifoPushValid: boolean;
  stickyFifoPushValue: number;
  stickyFifoPopValid: boolean;
  stickyFifoPopValue: number;
  stickyMatrixValid: boolean;
  stickyMatrixRow: number;
  stickyMatrixCol: number;
  stickyMatrixPressed: boolean;
};

export type AocVoiceSnapshot = {
  freq: number;
  pulseWidth: number;
  phaseAcc: number;
  noiseLfsr: number;
  envQ: number;
  ctrl: number;
  ad: number;
  sr: number;
  aux: number;
  phaseFracQ16: number;
  envCounter: number;
  envLevel: number;
  envState: number;
};

export type AocSnapshot = {
  voices: AocVoiceSnapshot[];
  tickReload: number;
  tickCounter: number;
  filterCutoff: number;
  lastSample: number;
  filterFQ16: number;
  filterDampQ16: number;
  filterLowQ16: number;
  filterBandQ16: number;
  modevol: number;
  irqEn: number;
  irqIfr: number;
  filterCtrl: number;
  filterRoute: number;
  pitchMode: number;
  osc3RoLatch: number;
  env3RoLatch: number;
  batchCount: number;
  synthCycleAccum: number;
  sampleCount: string;
};

export type TicChannelSnapshot = {
  count: number;
  latch: number;
  prescaleAccum: number;
  ier: number;
  ifr: number;
  prescaleSel: number;
  ctrlReg: number;
  en: boolean;
};

export type TicChipSnapshot = {
  icr: number;
  cra: number;
  crb: number;
  timerA: TicChannelSnapshot;
  timerB: TicChannelSnapshot;
};

export type TicSnapshot = {
  tic1: TicChipSnapshot;
  tic2: TicChipSnapshot;
  pra: number;
  prb: number;
  ddra: number;
  ddrb: number;
};

export type DicUnitSnapshot = {
  mounted: boolean;
  readOnly: boolean;
  dirty: boolean;
  allocHintTrack: number;
  allocHintSector: number;
  mmioCmd: number;
  mmioCtrl: number;
  mmioStatus: number;
  mmioErr: number;
  mmioDescLo: number;
  mmioDescHi: number;
  mmioRes0: number;
  mmioRes1: number;
  mmioStreamMode: number;
  mmioStreamOverwrite: boolean;
  mmioStreamSectorTrack: number;
  mmioStreamSectorNumber: number;
  mmioStreamSectorPayloadLen: number;
  mmioStreamSectorPayloadPos: number;
  mmioStreamNextTrack: number;
  mmioStreamNextSector: number;
  mmioStreamDirSector: number;
  mmioStreamDirSlot: number;
  mmioJobState: number;
  mmioJobCmd: number;
  mmioJobDescPolicy: number;
  mmioJobDescIndex: number;
  mmioJobResult: number;
  mmioDescValid: boolean;
  mmioAuxNameValid: boolean;
  mmioDescStreamOutActive: boolean;
  mmioDescStreamOutPos: number;
  mmioJobDescAvailable: boolean;
  mmioJobDescDirty: boolean;
  labelBytes: Uint8Array;
  streamNameBytes: Uint8Array;
  descriptorBytes: Uint8Array;
  freeBlocks: number;
  mmioStreamLen: number;
  mmioStreamPos: number;
  mmioStreamLoadAddr: number;
  mmioStreamAllocCount: number;
  mmioStreamAllocIndex: number;
  mmioStreamAllocWritten: number;
  mmioJobDescAddr: number;
  mmioStreamExpectedBytes: number;
  mmioStreamWrittenBytes: number;
};

export type DicSnapshot = {
  selectedUnit: StorageUnit;
  units: [DicUnitSnapshot, DicUnitSnapshot];
};

export type BreakpointInfo = {
  text: string;
  anchorAddress: number | null;
};

export const DEBUG_RULE_ACTIONS = ["break", "trace", "trace_stop"] as const;

export type DebugRuleAction = (typeof DEBUG_RULE_ACTIONS)[number];

export type DebuggerRuleSpec = {
  action: DebugRuleAction;
  everyNth: number;
  logText: string | null;
  matchText: string;
  maxEvents: number;
  name: string | null;
};

export type DebuggerRuleInfo = {
  action: DebugRuleAction;
  anchorAddress: number | null;
  everyNth: number;
  hitCount: number;
  logText: string;
  matchText: string;
  maxEvents: number;
  name: string;
  traceCount: number;
};

export type TraceLogEntry = {
  cycleCount: string;
  frameNo: number;
  pc: number;
  ruleName: string;
  sequenceNo: string;
  values: string;
};

export const DEBUG_STOP_REASONS = ["none", "manual", "breakpoint", "step", "halted"] as const;
export const DEBUG_PHASE_NAMES = [
  "FETCH",
  "READ_OP1",
  "READ_OP2",
  "ZPX_IDLE",
  "INDIRECT_PRE_IDLE",
  "INDIRECT_READ_LO",
  "INDIRECT_READ_HI",
  "INDIRECT_POST_IDLE",
  "IND_ABS_READ_LO",
  "IND_ABS_READ_HI",
  "PAGECROSS_IDLE",
  "MEM_READ",
  "MEM_WRITE",
  "EXEC",
  "BRANCH_EVAL",
  "BRANCH_TAKEN_IDLE",
  "BRANCH_PAGE_IDLE",
  "PUSH_REG",
  "POP_REG",
  "JSR_PUSH_PCH",
  "JSR_PUSH_PCL",
  "JSR_SET_PC",
  "RTS_POP_PCL",
  "RTS_POP_PCH",
  "RTS_INC_PC",
  "RTI_POP_P",
  "RTI_POP_PCL",
  "RTI_POP_PCH",
  "TRAP_PUSH_PCH",
  "TRAP_PUSH_PCL",
  "TRAP_PUSH_P",
  "TRAP_SET_I",
  "TRAP_VEC_LO",
  "TRAP_VEC_HI",
  "HALTED"
] as const;

export type DebugStopReason = (typeof DEBUG_STOP_REASONS)[number];
export type DebugPhaseName = (typeof DEBUG_PHASE_NAMES)[number] | "UNKNOWN";

type Am8ModuleFactory = () => Promise<Am8Module>;

type Am8Module = {
  HEAPU8: Uint8Array;
  _malloc(size: number): number;
  _free(ptr: number): void;
  _am8_web_create(): number;
  _am8_web_destroy(ptr: number): void;
  _am8_web_load_rom(ptr: number, kind: number, dataPtr: number, size: number): number;
  _am8_web_copy_rom(ptr: number, kind: number, offset: number, dst: number, dstSize: number): number;
  _am8_web_reset(ptr: number): void;
  _am8_web_run_cycles(ptr: number, cycles: bigint): bigint;
  _am8_web_get_state(ptr: number, outStatePtr: number): number;
  _am8_web_get_cycle_count(ptr: number): bigint;
  _am8_web_get_frame_no(ptr: number): number;
  _am8_web_is_halted(ptr: number): number;
  _am8_web_get_voc_state(ptr: number, outStatePtr: number): number;
  _am8_web_get_ioc_state(ptr: number, outStatePtr: number): number;
  _am8_web_clear_ioc_latches(ptr: number): void;
  _am8_web_get_aoc_state(ptr: number, outStatePtr: number): number;
  _am8_web_get_tic_state(ptr: number, outStatePtr: number): number;
  _am8_web_get_dic_state(ptr: number, outStatePtr: number): number;
  _am8_web_debug_pause(ptr: number): void;
  _am8_web_debug_continue(ptr: number): void;
  _am8_web_debug_step(ptr: number, count: bigint): number;
  _am8_web_debug_parse_breakpoint_expr(exprPtr: number, outInfoPtr: number): number;
  _am8_web_debug_parse_rule(
    action: number,
    matchExprPtr: number,
    logTextPtr: number,
    namePtr: number,
    everyNth: number,
    maxEvents: number,
    outInfoPtr: number
  ): number;
  _am8_web_debug_add_breakpoint(ptr: number, addr: number): number;
  _am8_web_debug_add_breakpoint_expr(ptr: number, exprPtr: number): number;
  _am8_web_debug_add_rule(
    ptr: number,
    action: number,
    matchExprPtr: number,
    logTextPtr: number,
    namePtr: number,
    everyNth: number,
    maxEvents: number
  ): number;
  _am8_web_debug_remove_breakpoint(ptr: number, addr: number): number;
  _am8_web_debug_remove_breakpoint_expr(ptr: number, exprPtr: number): number;
  _am8_web_debug_remove_rule_at(ptr: number, index: number): number;
  _am8_web_debug_clear_breakpoints(ptr: number): void;
  _am8_web_debug_get_breakpoint_count(ptr: number): number;
  _am8_web_debug_copy_breakpoints(ptr: number, dst: number, maxCount: number): number;
  _am8_web_debug_get_last_breakpoint_info(ptr: number, outInfoPtr: number): number;
  _am8_web_debug_clear_rules(ptr: number): void;
  _am8_web_debug_get_rule_count(ptr: number): number;
  _am8_web_debug_copy_rules(ptr: number, dst: number, maxCount: number): number;
  _am8_web_debug_get_last_rule_info(ptr: number, outInfoPtr: number): number;
  _am8_web_debug_set_trace_enabled(ptr: number, enabled: number): void;
  _am8_web_debug_trace_is_enabled(ptr: number): number;
  _am8_web_debug_clear_trace_log(ptr: number): void;
  _am8_web_debug_get_trace_log_count(ptr: number): number;
  _am8_web_debug_copy_trace_log(ptr: number, dst: number, maxCount: number): number;
  _am8_web_debug_get_stop_reason(ptr: number): number;
  _am8_web_debug_get_phase(ptr: number): number;
  _am8_web_debug_get_instruction_pc(ptr: number): number;
  _am8_web_debug_run(ptr: number, cycles: bigint): bigint;
  _am8_web_push_input_byte(ptr: number, value: number): number;
  _am8_web_key_event(ptr: number, key: number, pressed: number): number;
  _am8_web_drain_serial_tx(ptr: number, dst: number, dstSize: number): number;
  _am8_web_audio_source_rate_hz(): number;
  _am8_web_audio_available_frames(ptr: number): number;
  _am8_web_copy_audio_frames(ptr: number, dst: number, dstFrames: number): number;
  _am8_web_framebuffer_data(ptr: number): number;
  _am8_web_framebuffer_size(): number;
  _am8_web_memory_size(): number;
  _am8_web_disk_image_size(): number;
  _am8_web_copy_memory(ptr: number, addr: number, dst: number, dstSize: number): number;
  _am8_web_write_memory(ptr: number, addr: number, src: number, srcSize: number): number;
  _am8_web_detect_mode(
    mnemonicPtr: number,
    operandPtr: number,
    strictRoundtrip: number,
    modePtr: number,
    valuePtr: number,
    flagsPtr: number
  ): number;
  _am8_web_encode_instruction(
    mnemonicPtr: number,
    mode: number,
    pc: number,
    hasValue: number,
    value: bigint,
    dstPtr: number,
    dstSize: number,
    outSizePtr: number
  ): number;
  _am8_web_eval_directive(
    kind: number,
    operandPtr: number,
    currentPc: bigint,
    dstPtr: number,
    dstSize: number,
    outWrittenPtr: number,
    outNextPcPtr: number
  ): number;
  _am8_web_assemble_source_alloc(
    sourcePtr: number,
    outBasePtr: number,
    outSizePtr: number,
    outErrorPtrPtr: number
  ): number;
  _am8_web_disassemble_alloc(dataPtr: number, dataSize: number, base: number): number;
  _am8_web_storage_is_dirty(ptr: number, unit: number): number;
  _am8_web_storage_write_counter(ptr: number, unit: number): number;
  _am8_web_mount_disk_image(
    ptr: number,
    unit: number,
    dataPtr: number,
    size: number,
    readOnly: number
  ): number;
  _am8_web_sync_storage(ptr: number, unit: number): number;
  _am8_web_eject_storage(ptr: number, unit: number, force: number): number;
  _am8_web_copy_disk_image(ptr: number, unit: number, dst: number, dstSize: number): number;
};

const CORE_ERROR_TEXT: Record<number, string> = {
  1: "bad argument",
  2: "size out of range"
};

const STORAGE_ERROR_TEXT: Record<number, string> = {
  1: "not mounted",
  2: "already mounted",
  3: "read only",
  4: "bad argument",
  5: "name invalid",
  6: "name exists",
  7: "not found",
  8: "directory full",
  9: "no space",
  10: "buffer too small",
  11: "image format invalid",
  12: "image corrupt",
  13: "I/O error",
  14: "device busy"
};

const WORKBENCH_MODE_DETECT_ERROR_TEXT: Record<number, string> = {
  1: "bad workbench mode-detect arguments",
  2: "workbench mode-detect fallback"
};

const WORKBENCH_DIRECTIVE_ERROR_TEXT: Record<number, string> = {
  1: "bad workbench directive arguments",
  2: "workbench directive fallback",
  3: "workbench directive output exceeds available buffer"
};

const WORKBENCH_ASM_ERROR_TEXT: Record<number, string> = {
  1: "bad workbench assembler arguments",
  2: "unsupported mnemonic or addressing mode",
  3: "operand required",
  4: "branch out of range",
  5: "workbench assembler output buffer too small"
};

const WORKBENCH_MODE_FLAG_HAS_VALUE = 1 << 0;
const WORKBENCH_MODE_FLAG_PREFER_ABS = 1 << 1;
const AUDIO_SCRATCH_CAPACITY = 4096;
const SERIAL_SCRATCH_CAPACITY = 4096;
const CORE_STATE_STRUCT_SIZE = 32;
const CORE_STATE_PC_OFFSET = 0;
const CORE_STATE_A_OFFSET = 2;
const CORE_STATE_X_OFFSET = 3;
const CORE_STATE_Y_OFFSET = 4;
const CORE_STATE_SP_OFFSET = 5;
const CORE_STATE_P_OFFSET = 6;
const CORE_STATE_FRAME_NO_OFFSET = 8;
const CORE_STATE_CYCLES_OFFSET = 16;
const CORE_STATE_HALTED_OFFSET = 24;
const CORE_STATE_INSTR_BOUNDARY_OFFSET = 25;
const CORE_STATE_IRQ_LINE_OFFSET = 27;
const CORE_STATE_NMI_LINE_OFFSET = 28;
// Public web-bridge snapshot layouts; sizes are mirrored from `_Static_assert`s in `src/web_bridge.c`.
const VOC_STATE_STRUCT_SIZE = 160;
const VOC_STATE_SPRITE_X_OFFSET = 17;
const VOC_STATE_SPRITE_Y_OFFSET = 25;
const VOC_STATE_SPRITE_COLOR_OFFSET = 33;
const VOC_STATE_SPRITE_PTR_OFFSET = 41;
const VOC_STATE_PALETTE_OFFSET = 50;
const VOC_STATE_RASTER_IRQ_COMPARE_OFFSET = 82;
const VOC_STATE_RASTER_LINE_OFFSET = 84;
const VOC_STATE_RASTER_CYCLE_OFFSET = 86;
const VOC_STATE_FRAME_NO_OFFSET = 88;
const VOC_STATE_BADLINE_ACTIVE_OFFSET = 90;
const VOC_STATE_BADLINE_ROW_OFFSET = 91;
const VOC_STATE_PHI_HIGH_OWNER_OFFSET = 92;
const VOC_STATE_PHI_HIGH_REASON_OFFSET = 93;
const VOC_STATE_LOOKAHEAD_CYCLES_OFFSET = 94;
const VOC_STATE_NEXT_HIGH_FETCH_DISTANCE_OFFSET = 95;
const VOC_STATE_BA_LOW_OFFSET = 96;
const VOC_STATE_AEC_LOW_OFFSET = 97;
const VOC_STATE_GFX_LINE_VALID_OFFSET = 98;
const VOC_STATE_GFX_LINE_VISIBLE_ROW_OFFSET = 99;
const VOC_STATE_BORDER_COLOR_OFFSET = 100;
const VOC_STATE_BG_COLOR_OFFSET = 101;
const VOC_STATE_PHI_LOW_VIDEO_COUNT_OFFSET = 104;
const VOC_STATE_PHI_HIGH_CPU_COUNT_OFFSET = 112;
const VOC_STATE_PHI_HIGH_VIDEO_COUNT_OFFSET = 120;
const VOC_STATE_BADLINE_COUNT_OFFSET = 128;
const VOC_STATE_SPRITE_DMA_COUNT_OFFSET = 136;
const VOC_STATE_SLOT_FETCH_COUNT_OFFSET = 144;
const VOC_STATE_STALL_COUNT_OFFSET = 152;
const IOC_STATE_STRUCT_SIZE = 72;
const IOC_STATE_KEY_MATRIX_OFFSET = 17;
const IOC_STATE_FIFO_HEAD_OFFSET = 26;
const IOC_STATE_FIFO_TAIL_OFFSET = 28;
const IOC_STATE_FIFO_COUNT_OFFSET = 30;
const IOC_STATE_STICKY_FIFO_PUSH_CYCLE_COUNT_OFFSET = 32;
const IOC_STATE_STICKY_FIFO_POP_CYCLE_COUNT_OFFSET = 40;
const IOC_STATE_STICKY_MATRIX_CYCLE_COUNT_OFFSET = 48;
const IOC_STATE_STICKY_FIFO_PUSH_FRAME_NO_OFFSET = 56;
const IOC_STATE_STICKY_FIFO_POP_FRAME_NO_OFFSET = 58;
const IOC_STATE_STICKY_MATRIX_FRAME_NO_OFFSET = 60;
const IOC_STATE_STICKY_FIFO_COUNT_PEAK_OFFSET = 62;
const IOC_STATE_STICKY_FIFO_PUSH_VALID_OFFSET = 64;
const IOC_STATE_STICKY_FIFO_PUSH_VALUE_OFFSET = 65;
const IOC_STATE_STICKY_FIFO_POP_VALID_OFFSET = 66;
const IOC_STATE_STICKY_FIFO_POP_VALUE_OFFSET = 67;
const IOC_STATE_STICKY_MATRIX_VALID_OFFSET = 68;
const IOC_STATE_STICKY_MATRIX_ROW_OFFSET = 69;
const IOC_STATE_STICKY_MATRIX_COL_OFFSET = 70;
const IOC_STATE_STICKY_MATRIX_PRESSED_OFFSET = 71;
const AOC_VOICE_STATE_STRUCT_SIZE = 28;
const AOC_STATE_STRUCT_SIZE = 160;
const AOC_STATE_TICK_RELOAD_OFFSET = 112;
const AOC_STATE_TICK_COUNTER_OFFSET = 114;
const AOC_STATE_FILTER_CUTOFF_OFFSET = 116;
const AOC_STATE_LAST_SAMPLE_BITS_OFFSET = 118;
const AOC_STATE_FILTER_F_Q16_OFFSET = 120;
const AOC_STATE_FILTER_DAMP_Q16_OFFSET = 124;
const AOC_STATE_FILTER_LOW_Q16_BITS_OFFSET = 128;
const AOC_STATE_FILTER_BAND_Q16_BITS_OFFSET = 132;
const AOC_STATE_MODEVOL_OFFSET = 136;
const AOC_STATE_IRQ_EN_OFFSET = 137;
const AOC_STATE_IRQ_IFR_OFFSET = 138;
const AOC_STATE_FILTER_CTRL_OFFSET = 139;
const AOC_STATE_FILTER_ROUTE_OFFSET = 140;
const AOC_STATE_PITCH_MODE_OFFSET = 141;
const AOC_STATE_OSC3_RO_LATCH_OFFSET = 142;
const AOC_STATE_ENV3_RO_LATCH_OFFSET = 143;
const AOC_STATE_BATCH_COUNT_OFFSET = 144;
const AOC_STATE_SYNTH_CYCLE_ACCUM_OFFSET = 148;
const AOC_STATE_SAMPLE_COUNT_OFFSET = 152;
const TIC_CHANNEL_STATE_STRUCT_SIZE = 12;
const TIC_CHIP_STATE_STRUCT_SIZE = 28;
const TIC_STATE_STRUCT_SIZE = 60;
const TIC_CHIP_TIMER_A_OFFSET = 4;
const TIC_CHIP_TIMER_B_OFFSET = 16;
const TIC_STATE_TIC2_OFFSET = 28;
const TIC_STATE_PRA_OFFSET = 56;
const TIC_STATE_PRB_OFFSET = 57;
const TIC_STATE_DDRA_OFFSET = 58;
const TIC_STATE_DDRB_OFFSET = 59;
const DIC_UNIT_STATE_STRUCT_SIZE = 124;
const DIC_STATE_STRUCT_SIZE = 252;
const DIC_STATE_UNIT0_OFFSET = 4;
const DIC_STATE_UNIT1_OFFSET = 128;
const DIC_UNIT_LABEL_OFFSET = 34;
const DIC_UNIT_STREAM_NAME_OFFSET = 50;
const DIC_UNIT_DESCRIPTOR_OFFSET = 66;
const DIC_UNIT_FREE_BLOCKS_OFFSET = 98;
const DIC_UNIT_STREAM_LEN_OFFSET = 100;
const DIC_UNIT_STREAM_POS_OFFSET = 102;
const DIC_UNIT_STREAM_LOAD_ADDR_OFFSET = 104;
const DIC_UNIT_STREAM_ALLOC_COUNT_OFFSET = 106;
const DIC_UNIT_STREAM_ALLOC_INDEX_OFFSET = 108;
const DIC_UNIT_STREAM_ALLOC_WRITTEN_OFFSET = 110;
const DIC_UNIT_JOB_DESC_ADDR_OFFSET = 112;
const DIC_UNIT_STREAM_EXPECTED_BYTES_OFFSET = 114;
const DIC_UNIT_STREAM_WRITTEN_BYTES_OFFSET = 118;
const BREAKPOINT_CAPACITY = 64;
const BREAKPOINT_INFO_STRUCT_SIZE = 100;
const BREAKPOINT_INFO_HAS_ANCHOR_OFFSET = 2;
const BREAKPOINT_INFO_TEXT_OFFSET = 4;
const BREAKPOINT_INFO_TEXT_LENGTH = 96;
const DEBUG_RULE_INFO_STRUCT_SIZE = 240;
const DEBUG_RULE_INFO_HAS_ANCHOR_OFFSET = 2;
const DEBUG_RULE_INFO_ACTION_OFFSET = 3;
const DEBUG_RULE_INFO_EVERY_NTH_OFFSET = 4;
const DEBUG_RULE_INFO_MAX_EVENTS_OFFSET = 6;
const DEBUG_RULE_INFO_HIT_COUNT_OFFSET = 8;
const DEBUG_RULE_INFO_TRACE_COUNT_OFFSET = 12;
const DEBUG_RULE_INFO_NAME_OFFSET = 16;
const DEBUG_RULE_INFO_NAME_LENGTH = 32;
const DEBUG_RULE_INFO_MATCH_TEXT_OFFSET = 48;
const DEBUG_RULE_INFO_MATCH_TEXT_LENGTH = 96;
const DEBUG_RULE_INFO_LOG_TEXT_OFFSET = 144;
const DEBUG_RULE_INFO_LOG_TEXT_LENGTH = 96;
const TRACE_LOG_ENTRY_STRUCT_SIZE = 216;
const TRACE_LOG_ENTRY_CYCLE_COUNT_OFFSET = 8;
const TRACE_LOG_ENTRY_FRAME_NO_OFFSET = 16;
const TRACE_LOG_ENTRY_PC_OFFSET = 18;
const TRACE_LOG_ENTRY_RULE_NAME_OFFSET = 24;
const TRACE_LOG_ENTRY_RULE_NAME_LENGTH = 32;
const TRACE_LOG_ENTRY_VALUES_OFFSET = 56;
const TRACE_LOG_ENTRY_VALUES_LENGTH = 160;
const ASCII_ENCODER = new TextEncoder();
const ASCII_DECODER = new TextDecoder();

async function loadModuleFactory(): Promise<Am8ModuleFactory> {
  const moduleUrl = new URL("./am8web.js", import.meta.url).href;
  const imported = (await import(moduleUrl)) as { default?: Am8ModuleFactory };
  const factory = imported.default;
  if (typeof factory !== "function") {
    throw new Error("am8web.js did not export an Emscripten module factory");
  }
  return factory;
}

function throwCoreError(context: string, rc: number): never {
  const detail = CORE_ERROR_TEXT[rc] ?? `unexpected error ${rc}`;
  throw new Error(`${context}: ${detail}`);
}

function throwStorageError(context: string, rc: number): never {
  const detail = STORAGE_ERROR_TEXT[rc] ?? `unexpected error ${rc}`;
  throw new Error(`${context}: ${detail}`);
}

function readBigInt64(module: Am8Module, ptr: number): bigint {
  return new DataView(module.HEAPU8.buffer).getBigInt64(ptr, true);
}

function readBigUint64(module: Am8Module, ptr: number): bigint {
  return new DataView(module.HEAPU8.buffer).getBigUint64(ptr, true);
}

function readUint16(module: Am8Module, ptr: number): number {
  return new DataView(module.HEAPU8.buffer).getUint16(ptr, true);
}

function readUint32(module: Am8Module, ptr: number): number {
  return new DataView(module.HEAPU8.buffer).getUint32(ptr, true);
}

function readInt16(module: Am8Module, ptr: number): number {
  return new DataView(module.HEAPU8.buffer).getInt16(ptr, true);
}

function readInt32(module: Am8Module, ptr: number): number {
  return new DataView(module.HEAPU8.buffer).getInt32(ptr, true);
}

function copyUint8Array(module: Am8Module, ptr: number, length: number): Uint8Array {
  return module.HEAPU8.slice(ptr, ptr + length);
}

function copyUint16Array(module: Am8Module, ptr: number, count: number): Uint16Array {
  const values = new Uint16Array(count);
  for (let index = 0; index < count; index += 1) {
    values[index] = readUint16(module, ptr + index * 2);
  }
  return values;
}

function readFixedCString(module: Am8Module, ptr: number, length: number): string {
  let end = ptr;
  const maxEnd = ptr + length;
  while (end < maxEnd && module.HEAPU8[end] !== 0) {
    end += 1;
  }
  return ASCII_DECODER.decode(module.HEAPU8.slice(ptr, end));
}

function decodeBreakpointInfo(module: Am8Module, ptr: number): BreakpointInfo {
  const hasAnchor = module.HEAPU8[ptr + BREAKPOINT_INFO_HAS_ANCHOR_OFFSET] !== 0;
  return {
    text: readFixedCString(module, ptr + BREAKPOINT_INFO_TEXT_OFFSET, BREAKPOINT_INFO_TEXT_LENGTH),
    anchorAddress: hasAnchor ? readUint16(module, ptr) : null
  };
}

function encodeDebugRuleAction(action: DebugRuleAction): number {
  switch (action) {
    case "break":
      return 0;
    case "trace":
      return 1;
    case "trace_stop":
      return 2;
  }
}

function decodeDebugRuleAction(value: number): DebugRuleAction {
  return DEBUG_RULE_ACTIONS[value] ?? "break";
}

function decodeDebuggerRuleInfo(module: Am8Module, ptr: number): DebuggerRuleInfo {
  const hasAnchor = module.HEAPU8[ptr + DEBUG_RULE_INFO_HAS_ANCHOR_OFFSET] !== 0;
  return {
    action: decodeDebugRuleAction(module.HEAPU8[ptr + DEBUG_RULE_INFO_ACTION_OFFSET]),
    anchorAddress: hasAnchor ? readUint16(module, ptr) : null,
    everyNth: readUint16(module, ptr + DEBUG_RULE_INFO_EVERY_NTH_OFFSET),
    hitCount: readUint32(module, ptr + DEBUG_RULE_INFO_HIT_COUNT_OFFSET),
    logText: readFixedCString(module, ptr + DEBUG_RULE_INFO_LOG_TEXT_OFFSET, DEBUG_RULE_INFO_LOG_TEXT_LENGTH),
    matchText: readFixedCString(module, ptr + DEBUG_RULE_INFO_MATCH_TEXT_OFFSET, DEBUG_RULE_INFO_MATCH_TEXT_LENGTH),
    maxEvents: readUint16(module, ptr + DEBUG_RULE_INFO_MAX_EVENTS_OFFSET),
    name: readFixedCString(module, ptr + DEBUG_RULE_INFO_NAME_OFFSET, DEBUG_RULE_INFO_NAME_LENGTH),
    traceCount: readUint32(module, ptr + DEBUG_RULE_INFO_TRACE_COUNT_OFFSET)
  };
}

function decodeTraceLogEntry(module: Am8Module, ptr: number): TraceLogEntry {
  return {
    cycleCount: readBigUint64(module, ptr + TRACE_LOG_ENTRY_CYCLE_COUNT_OFFSET).toString(),
    frameNo: readUint16(module, ptr + TRACE_LOG_ENTRY_FRAME_NO_OFFSET),
    pc: readUint16(module, ptr + TRACE_LOG_ENTRY_PC_OFFSET),
    ruleName: readFixedCString(module, ptr + TRACE_LOG_ENTRY_RULE_NAME_OFFSET, TRACE_LOG_ENTRY_RULE_NAME_LENGTH),
    sequenceNo: readBigUint64(module, ptr).toString(),
    values: readFixedCString(module, ptr + TRACE_LOG_ENTRY_VALUES_OFFSET, TRACE_LOG_ENTRY_VALUES_LENGTH)
  };
}

function decodeDebugStopReason(value: number): DebugStopReason {
  return DEBUG_STOP_REASONS[value] ?? "none";
}

function decodeDebugPhase(value: number): DebugPhaseName {
  return DEBUG_PHASE_NAMES[value] ?? "UNKNOWN";
}

function requireUint16Address(value: number, label: string): number {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} must be a finite integer`);
  }
  if (value < 0 || value > 0xffff) {
    throw new Error(`${label} must be in range 0x0000..0xFFFF`);
  }
  return value;
}

function requirePositiveCount(value: number, label: string): number {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} must be a finite integer`);
  }
  if (value <= 0) {
    throw new Error(`${label} must be >= 1`);
  }
  return value;
}

function requireNativeCount(value: number, label: string): number {
  if (!Number.isFinite(value) || !Number.isInteger(value)) {
    throw new Error(`${label} returned an invalid count`);
  }
  if (value < 0) {
    throw new Error(`${label} returned a negative count`);
  }
  return value;
}

function requireNativeCountAtMost(value: number, max: number, label: string): number {
  const count = requireNativeCount(value, label);
  if (count > max) {
    throw new Error(`${label} returned out-of-range count ${count} (max ${max})`);
  }
  return count;
}

function decodeAocVoice(module: Am8Module, ptr: number): AocVoiceSnapshot {
  return {
    freq: readUint16(module, ptr),
    pulseWidth: readUint16(module, ptr + 2),
    phaseAcc: readUint32(module, ptr + 4),
    noiseLfsr: readUint32(module, ptr + 8),
    envQ: readUint32(module, ptr + 12),
    ctrl: module.HEAPU8[ptr + 16],
    ad: module.HEAPU8[ptr + 17],
    sr: module.HEAPU8[ptr + 18],
    aux: module.HEAPU8[ptr + 19],
    phaseFracQ16: readUint16(module, ptr + 20),
    envCounter: readUint16(module, ptr + 22),
    envLevel: module.HEAPU8[ptr + 24],
    envState: module.HEAPU8[ptr + 25]
  };
}

function decodeTicChannel(module: Am8Module, ptr: number): TicChannelSnapshot {
  return {
    count: readUint16(module, ptr),
    latch: readUint16(module, ptr + 2),
    prescaleAccum: readUint16(module, ptr + 4),
    ier: module.HEAPU8[ptr + 6],
    ifr: module.HEAPU8[ptr + 7],
    prescaleSel: module.HEAPU8[ptr + 8],
    ctrlReg: module.HEAPU8[ptr + 9],
    en: module.HEAPU8[ptr + 10] !== 0
  };
}

function decodeTicChip(module: Am8Module, ptr: number): TicChipSnapshot {
  return {
    icr: module.HEAPU8[ptr],
    cra: module.HEAPU8[ptr + 1],
    crb: module.HEAPU8[ptr + 2],
    timerA: decodeTicChannel(module, ptr + TIC_CHIP_TIMER_A_OFFSET),
    timerB: decodeTicChannel(module, ptr + TIC_CHIP_TIMER_B_OFFSET)
  };
}

function decodeDicUnit(module: Am8Module, ptr: number): DicUnitSnapshot {
  return {
    mounted: module.HEAPU8[ptr] !== 0,
    readOnly: module.HEAPU8[ptr + 1] !== 0,
    dirty: module.HEAPU8[ptr + 2] !== 0,
    allocHintTrack: module.HEAPU8[ptr + 3],
    allocHintSector: module.HEAPU8[ptr + 4],
    mmioCmd: module.HEAPU8[ptr + 5],
    mmioCtrl: module.HEAPU8[ptr + 6],
    mmioStatus: module.HEAPU8[ptr + 7],
    mmioErr: module.HEAPU8[ptr + 8],
    mmioDescLo: module.HEAPU8[ptr + 9],
    mmioDescHi: module.HEAPU8[ptr + 10],
    mmioRes0: module.HEAPU8[ptr + 11],
    mmioRes1: module.HEAPU8[ptr + 12],
    mmioStreamMode: module.HEAPU8[ptr + 13],
    mmioStreamOverwrite: module.HEAPU8[ptr + 14] !== 0,
    mmioStreamSectorTrack: module.HEAPU8[ptr + 15],
    mmioStreamSectorNumber: module.HEAPU8[ptr + 16],
    mmioStreamSectorPayloadLen: module.HEAPU8[ptr + 17],
    mmioStreamSectorPayloadPos: module.HEAPU8[ptr + 18],
    mmioStreamNextTrack: module.HEAPU8[ptr + 19],
    mmioStreamNextSector: module.HEAPU8[ptr + 20],
    mmioStreamDirSector: module.HEAPU8[ptr + 21],
    mmioStreamDirSlot: module.HEAPU8[ptr + 22],
    mmioJobState: module.HEAPU8[ptr + 23],
    mmioJobCmd: module.HEAPU8[ptr + 24],
    mmioJobDescPolicy: module.HEAPU8[ptr + 25],
    mmioJobDescIndex: module.HEAPU8[ptr + 26],
    mmioJobResult: module.HEAPU8[ptr + 27],
    mmioDescValid: module.HEAPU8[ptr + 28] !== 0,
    mmioAuxNameValid: module.HEAPU8[ptr + 29] !== 0,
    mmioDescStreamOutActive: module.HEAPU8[ptr + 30] !== 0,
    mmioDescStreamOutPos: module.HEAPU8[ptr + 31],
    mmioJobDescAvailable: module.HEAPU8[ptr + 32] !== 0,
    mmioJobDescDirty: module.HEAPU8[ptr + 33] !== 0,
    labelBytes: copyUint8Array(module, ptr + DIC_UNIT_LABEL_OFFSET, 16),
    streamNameBytes: copyUint8Array(module, ptr + DIC_UNIT_STREAM_NAME_OFFSET, 16),
    descriptorBytes: copyUint8Array(module, ptr + DIC_UNIT_DESCRIPTOR_OFFSET, 32),
    freeBlocks: readUint16(module, ptr + DIC_UNIT_FREE_BLOCKS_OFFSET),
    mmioStreamLen: readUint16(module, ptr + DIC_UNIT_STREAM_LEN_OFFSET),
    mmioStreamPos: readUint16(module, ptr + DIC_UNIT_STREAM_POS_OFFSET),
    mmioStreamLoadAddr: readUint16(module, ptr + DIC_UNIT_STREAM_LOAD_ADDR_OFFSET),
    mmioStreamAllocCount: readUint16(module, ptr + DIC_UNIT_STREAM_ALLOC_COUNT_OFFSET),
    mmioStreamAllocIndex: readUint16(module, ptr + DIC_UNIT_STREAM_ALLOC_INDEX_OFFSET),
    mmioStreamAllocWritten: readUint16(module, ptr + DIC_UNIT_STREAM_ALLOC_WRITTEN_OFFSET),
    mmioJobDescAddr: readUint16(module, ptr + DIC_UNIT_JOB_DESC_ADDR_OFFSET),
    mmioStreamExpectedBytes: readUint32(module, ptr + DIC_UNIT_STREAM_EXPECTED_BYTES_OFFSET),
    mmioStreamWrittenBytes: readUint32(module, ptr + DIC_UNIT_STREAM_WRITTEN_BYTES_OFFSET)
  };
}

export class Emulator {
  private readonly module: Am8Module;
  private readonly diskImageSize: number;
  private readonly memorySize: number;
  private webPtr: number;
  private readonly audioScratchPtr: number;
  private readonly serialScratchPtr: number;

  private constructor(
    module: Am8Module,
    webPtr: number,
    audioScratchPtr: number,
    serialScratchPtr: number,
    memorySize: number,
    diskImageSize: number
  ) {
    this.module = module;
    this.diskImageSize = diskImageSize;
    this.memorySize = memorySize;
    this.webPtr = webPtr;
    this.audioScratchPtr = audioScratchPtr;
    this.serialScratchPtr = serialScratchPtr;
  }

  static async create(): Promise<Emulator> {
    const factory = await loadModuleFactory();
    const module = await factory();
    const webPtr = module._am8_web_create();
    if (webPtr === 0) {
      throw new Error("failed to create AM-8 web bridge instance");
    }

    const audioScratchPtr = module._malloc(AUDIO_SCRATCH_CAPACITY * 2);
    if (audioScratchPtr === 0) {
      module._am8_web_destroy(webPtr);
      throw new Error("failed to allocate audio scratch buffer");
    }

    const serialScratchPtr = module._malloc(SERIAL_SCRATCH_CAPACITY);
    if (serialScratchPtr === 0) {
      module._free(audioScratchPtr);
      module._am8_web_destroy(webPtr);
      throw new Error("failed to allocate serial scratch buffer");
    }

    if (module._am8_web_framebuffer_size() !== FRAMEBUFFER_SIZE) {
      module._free(audioScratchPtr);
      module._free(serialScratchPtr);
      module._am8_web_destroy(webPtr);
      throw new Error("unexpected framebuffer size from web bridge");
    }

    const memorySize = module._am8_web_memory_size();
    if (memorySize !== MEMORY_SIZE) {
      module._free(audioScratchPtr);
      module._free(serialScratchPtr);
      module._am8_web_destroy(webPtr);
      throw new Error("unexpected memory size from web bridge");
    }

    const diskImageSize = module._am8_web_disk_image_size();
    if (diskImageSize <= 0) {
      module._free(audioScratchPtr);
      module._free(serialScratchPtr);
      module._am8_web_destroy(webPtr);
      throw new Error("unexpected disk image size from web bridge");
    }

    return new Emulator(module, webPtr, audioScratchPtr, serialScratchPtr, memorySize, diskImageSize);
  }

  destroy(): void {
    if (this.webPtr === 0) {
      return;
    }

    this.module._free(this.audioScratchPtr);
    this.module._free(this.serialScratchPtr);
    this.module._am8_web_destroy(this.webPtr);
    this.webPtr = 0;
  }

  private requireLivePtr(): number {
    if (this.webPtr === 0) {
      throw new Error("emulator instance is not available");
    }
    return this.webPtr;
  }

  private withAsciiString<T>(text: string | null, callback: (ptr: number) => T): T {
    if (text === null) {
      return callback(0);
    }

    const encoded = ASCII_ENCODER.encode(text);
    const ptr = this.module._malloc(encoded.length + 1);
    if (ptr === 0) {
      throw new Error("failed to allocate string buffer in Wasm memory");
    }

    try {
      this.module.HEAPU8.set(encoded, ptr);
      this.module.HEAPU8[ptr + encoded.length] = 0;
      return callback(ptr);
    } finally {
      this.module._free(ptr);
    }
  }

  private withRuleStrings<T>(
    matchText: string,
    logText: string | null,
    name: string | null,
    callback: (matchPtr: number, logPtr: number, namePtr: number) => T
  ): T {
    return this.withAsciiString(matchText, (matchPtr) =>
      this.withAsciiString(logText, (logPtr) => this.withAsciiString(name, (namePtr) => callback(matchPtr, logPtr, namePtr)))
    );
  }

  private readCString(ptr: number): string {
    let end = ptr;
    while (this.module.HEAPU8[end] !== 0) {
      end += 1;
    }
    return ASCII_DECODER.decode(this.module.HEAPU8.slice(ptr, end));
  }

  loadRom(kind: RomKind, data: Uint8Array): void {
    const webPtr = this.requireLivePtr();
    if (data.length === 0) {
      throw new Error("ROM data must not be empty");
    }

    const dataPtr = this.module._malloc(data.length);
    if (dataPtr === 0) {
      throw new Error("failed to allocate ROM buffer in Wasm memory");
    }

    try {
      this.module.HEAPU8.set(data, dataPtr);
      const rc = this.module._am8_web_load_rom(webPtr, kind, dataPtr, data.length);
      if (rc !== 0) {
        throwCoreError("failed to load ROM", rc);
      }
    } finally {
      this.module._free(dataPtr);
    }
  }

  async loadRomFromUrl(kind: RomKind, url: string): Promise<void> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`failed to fetch ROM ${url}: ${response.status} ${response.statusText}`);
    }

    this.loadRom(kind, new Uint8Array(await response.arrayBuffer()));
  }

  copyRom(kind: RomKind, offset: number, length: number): Uint8Array {
    if (!Number.isFinite(offset) || !Number.isInteger(offset)) {
      throw new Error("ROM offset must be a finite integer");
    }
    if (!Number.isFinite(length) || !Number.isInteger(length)) {
      throw new Error("ROM copy length must be a finite integer");
    }

    const romOffset = Math.floor(offset);
    const byteLength = Math.floor(length);
    if (romOffset < 0 || romOffset > 0xffffffff) {
      throw new Error("ROM offset must be in range 0x00000000..0xFFFFFFFF");
    }
    if (byteLength < 0 || byteLength > 0xffffffff) {
      throw new Error("ROM copy length must be in range 0..0xFFFFFFFF");
    }
    if (byteLength === 0) {
      return new Uint8Array(0);
    }

    const dstPtr = this.module._malloc(byteLength);
    if (dstPtr === 0) {
      throw new Error("failed to allocate ROM snapshot buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_copy_rom(this.requireLivePtr(), kind, romOffset, dstPtr, byteLength);
      if (ok === 0) {
        throw new Error(
          `failed to copy ROM kind ${kind} at offset 0x${romOffset.toString(16).toUpperCase()} length ${byteLength}`
        );
      }
      return this.module.HEAPU8.slice(dstPtr, dstPtr + byteLength);
    } finally {
      this.module._free(dstPtr);
    }
  }

  copyCharacterRom(offset: number, length: number): Uint8Array {
    return this.copyRom(ROM_KIND.char, offset, length);
  }

  reset(): void {
    this.module._am8_web_reset(this.requireLivePtr());
  }

  runCycles(cycles: number): number {
    const requested = Math.max(0, Math.floor(cycles));
    return Number(this.module._am8_web_run_cycles(this.requireLivePtr(), BigInt(requested)));
  }

  runDebugCycles(cycles: number): number {
    const requested = Math.max(0, Math.floor(cycles));
    return Number(this.module._am8_web_debug_run(this.requireLivePtr(), BigInt(requested)));
  }

  getState(): CoreSnapshot {
    const statePtr = this.module._malloc(CORE_STATE_STRUCT_SIZE);
    if (statePtr === 0) {
      throw new Error("failed to allocate core-state buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_get_state(this.requireLivePtr(), statePtr);
      if (ok === 0) {
        throw new Error("failed to read core-state snapshot");
      }

      return {
        pc: readUint16(this.module, statePtr + CORE_STATE_PC_OFFSET),
        instrPc: this.module._am8_web_debug_get_instruction_pc(this.requireLivePtr()) & 0xffff,
        a: this.module.HEAPU8[statePtr + CORE_STATE_A_OFFSET],
        x: this.module.HEAPU8[statePtr + CORE_STATE_X_OFFSET],
        y: this.module.HEAPU8[statePtr + CORE_STATE_Y_OFFSET],
        sp: this.module.HEAPU8[statePtr + CORE_STATE_SP_OFFSET],
        p: this.module.HEAPU8[statePtr + CORE_STATE_P_OFFSET],
        frameNo: readUint16(this.module, statePtr + CORE_STATE_FRAME_NO_OFFSET),
        cycles: readBigUint64(this.module, statePtr + CORE_STATE_CYCLES_OFFSET).toString(),
        halted: this.module.HEAPU8[statePtr + CORE_STATE_HALTED_OFFSET] !== 0,
        instrBoundary: this.module.HEAPU8[statePtr + CORE_STATE_INSTR_BOUNDARY_OFFSET] !== 0,
        phase: decodeDebugPhase(this.module._am8_web_debug_get_phase(this.requireLivePtr())),
        irqLine: this.module.HEAPU8[statePtr + CORE_STATE_IRQ_LINE_OFFSET] !== 0,
        nmiLine: this.module.HEAPU8[statePtr + CORE_STATE_NMI_LINE_OFFSET] !== 0
      };
    } finally {
      this.module._free(statePtr);
    }
  }

  getVocState(): VocSnapshot {
    const statePtr = this.module._malloc(VOC_STATE_STRUCT_SIZE);
    if (statePtr === 0) {
      throw new Error("failed to allocate VOC-state buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_get_voc_state(this.requireLivePtr(), statePtr);
      if (ok === 0) {
        throw new Error("failed to read VOC-state snapshot");
      }

      return {
        ctrl1: this.module.HEAPU8[statePtr],
        raster: this.module.HEAPU8[statePtr + 1],
        ctrl2: this.module.HEAPU8[statePtr + 2],
        memPtr: this.module.HEAPU8[statePtr + 3],
        irqIfr: this.module.HEAPU8[statePtr + 4],
        irqIer: this.module.HEAPU8[statePtr + 5],
        spriteXMsb: this.module.HEAPU8[statePtr + 6],
        spriteEn: this.module.HEAPU8[statePtr + 7],
        spriteYExpand: this.module.HEAPU8[statePtr + 8],
        spritePriority: this.module.HEAPU8[statePtr + 9],
        spriteMulticolor: this.module.HEAPU8[statePtr + 10],
        spriteXExpand: this.module.HEAPU8[statePtr + 11],
        spriteColl: this.module.HEAPU8[statePtr + 12],
        spriteCollBg: this.module.HEAPU8[statePtr + 13],
        spriteMc0: this.module.HEAPU8[statePtr + 14],
        spriteMc1: this.module.HEAPU8[statePtr + 15],
        videoCtrl: this.module.HEAPU8[statePtr + 16],
        spriteX: copyUint8Array(this.module, statePtr + VOC_STATE_SPRITE_X_OFFSET, 8),
        spriteY: copyUint8Array(this.module, statePtr + VOC_STATE_SPRITE_Y_OFFSET, 8),
        spriteColor: copyUint8Array(this.module, statePtr + VOC_STATE_SPRITE_COLOR_OFFSET, 8),
        spritePtr: copyUint8Array(this.module, statePtr + VOC_STATE_SPRITE_PTR_OFFSET, 8),
        palette: copyUint16Array(this.module, statePtr + VOC_STATE_PALETTE_OFFSET, 16),
        rasterIrqCompare: readUint16(this.module, statePtr + VOC_STATE_RASTER_IRQ_COMPARE_OFFSET),
        rasterLine: readUint16(this.module, statePtr + VOC_STATE_RASTER_LINE_OFFSET),
        rasterCycle: readUint16(this.module, statePtr + VOC_STATE_RASTER_CYCLE_OFFSET),
        frameNo: readUint16(this.module, statePtr + VOC_STATE_FRAME_NO_OFFSET),
        badlineActive: this.module.HEAPU8[statePtr + VOC_STATE_BADLINE_ACTIVE_OFFSET] !== 0,
        badlineRow: this.module.HEAPU8[statePtr + VOC_STATE_BADLINE_ROW_OFFSET],
        phiHighOwner: this.module.HEAPU8[statePtr + VOC_STATE_PHI_HIGH_OWNER_OFFSET],
        phiHighReason: this.module.HEAPU8[statePtr + VOC_STATE_PHI_HIGH_REASON_OFFSET],
        lookaheadCycles: this.module.HEAPU8[statePtr + VOC_STATE_LOOKAHEAD_CYCLES_OFFSET],
        nextHighFetchDistance: this.module.HEAPU8[statePtr + VOC_STATE_NEXT_HIGH_FETCH_DISTANCE_OFFSET],
        baLow: this.module.HEAPU8[statePtr + VOC_STATE_BA_LOW_OFFSET] !== 0,
        aecLow: this.module.HEAPU8[statePtr + VOC_STATE_AEC_LOW_OFFSET] !== 0,
        gfxLineValid: this.module.HEAPU8[statePtr + VOC_STATE_GFX_LINE_VALID_OFFSET] !== 0,
        gfxLineVisibleRow: this.module.HEAPU8[statePtr + VOC_STATE_GFX_LINE_VISIBLE_ROW_OFFSET],
        borderColor: this.module.HEAPU8[statePtr + VOC_STATE_BORDER_COLOR_OFFSET],
        bgColor: this.module.HEAPU8[statePtr + VOC_STATE_BG_COLOR_OFFSET],
        phiLowVideoCount: readBigUint64(this.module, statePtr + VOC_STATE_PHI_LOW_VIDEO_COUNT_OFFSET).toString(),
        phiHighCpuCount: readBigUint64(this.module, statePtr + VOC_STATE_PHI_HIGH_CPU_COUNT_OFFSET).toString(),
        phiHighVideoCount: readBigUint64(this.module, statePtr + VOC_STATE_PHI_HIGH_VIDEO_COUNT_OFFSET).toString(),
        badlineCount: readBigUint64(this.module, statePtr + VOC_STATE_BADLINE_COUNT_OFFSET).toString(),
        spriteDmaCount: readBigUint64(this.module, statePtr + VOC_STATE_SPRITE_DMA_COUNT_OFFSET).toString(),
        slotFetchCount: readBigUint64(this.module, statePtr + VOC_STATE_SLOT_FETCH_COUNT_OFFSET).toString(),
        stallCount: readBigUint64(this.module, statePtr + VOC_STATE_STALL_COUNT_OFFSET).toString()
      };
    } finally {
      this.module._free(statePtr);
    }
  }

  getIocState(): IocSnapshot {
    const statePtr = this.module._malloc(IOC_STATE_STRUCT_SIZE);
    if (statePtr === 0) {
      throw new Error("failed to allocate IOC-state buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_get_ioc_state(this.requireLivePtr(), statePtr);
      if (ok === 0) {
        throw new Error("failed to read IOC-state snapshot");
      }

      return {
        kbdData: this.module.HEAPU8[statePtr],
        kbdStatus: this.module.HEAPU8[statePtr + 1],
        pra: this.module.HEAPU8[statePtr + 2],
        prb: this.module.HEAPU8[statePtr + 3],
        ddra: this.module.HEAPU8[statePtr + 4],
        ddrb: this.module.HEAPU8[statePtr + 5],
        joy1: this.module.HEAPU8[statePtr + 6],
        joy2: this.module.HEAPU8[statePtr + 7],
        mouseDx: this.module.HEAPU8[statePtr + 8] << 24 >> 24,
        mouseDy: this.module.HEAPU8[statePtr + 9] << 24 >> 24,
        mouseBtn: this.module.HEAPU8[statePtr + 10],
        videoCtrl: this.module.HEAPU8[statePtr + 11],
        icr: this.module.HEAPU8[statePtr + 12],
        ctrl: this.module.HEAPU8[statePtr + 13],
        idcap: this.module.HEAPU8[statePtr + 14],
        ier: this.module.HEAPU8[statePtr + 15],
        ifr: this.module.HEAPU8[statePtr + 16],
        keyMatrix: copyUint8Array(this.module, statePtr + IOC_STATE_KEY_MATRIX_OFFSET, 8),
        fifoHead: readUint16(this.module, statePtr + IOC_STATE_FIFO_HEAD_OFFSET),
        fifoTail: readUint16(this.module, statePtr + IOC_STATE_FIFO_TAIL_OFFSET),
        fifoCount: readUint16(this.module, statePtr + IOC_STATE_FIFO_COUNT_OFFSET),
        stickyFifoPushCycleCount: readBigUint64(this.module, statePtr + IOC_STATE_STICKY_FIFO_PUSH_CYCLE_COUNT_OFFSET).toString(),
        stickyFifoPopCycleCount: readBigUint64(this.module, statePtr + IOC_STATE_STICKY_FIFO_POP_CYCLE_COUNT_OFFSET).toString(),
        stickyMatrixCycleCount: readBigUint64(this.module, statePtr + IOC_STATE_STICKY_MATRIX_CYCLE_COUNT_OFFSET).toString(),
        stickyFifoPushFrameNo: readUint16(this.module, statePtr + IOC_STATE_STICKY_FIFO_PUSH_FRAME_NO_OFFSET),
        stickyFifoPopFrameNo: readUint16(this.module, statePtr + IOC_STATE_STICKY_FIFO_POP_FRAME_NO_OFFSET),
        stickyMatrixFrameNo: readUint16(this.module, statePtr + IOC_STATE_STICKY_MATRIX_FRAME_NO_OFFSET),
        stickyFifoCountPeak: readUint16(this.module, statePtr + IOC_STATE_STICKY_FIFO_COUNT_PEAK_OFFSET),
        stickyFifoPushValid: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_FIFO_PUSH_VALID_OFFSET] !== 0,
        stickyFifoPushValue: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_FIFO_PUSH_VALUE_OFFSET],
        stickyFifoPopValid: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_FIFO_POP_VALID_OFFSET] !== 0,
        stickyFifoPopValue: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_FIFO_POP_VALUE_OFFSET],
        stickyMatrixValid: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_MATRIX_VALID_OFFSET] !== 0,
        stickyMatrixRow: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_MATRIX_ROW_OFFSET],
        stickyMatrixCol: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_MATRIX_COL_OFFSET],
        stickyMatrixPressed: this.module.HEAPU8[statePtr + IOC_STATE_STICKY_MATRIX_PRESSED_OFFSET] !== 0
      };
    } finally {
      this.module._free(statePtr);
    }
  }

  clearIocLatches(): void {
    this.module._am8_web_clear_ioc_latches(this.requireLivePtr());
  }

  getAocState(): AocSnapshot {
    const statePtr = this.module._malloc(AOC_STATE_STRUCT_SIZE);
    if (statePtr === 0) {
      throw new Error("failed to allocate AOC-state buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_get_aoc_state(this.requireLivePtr(), statePtr);
      if (ok === 0) {
        throw new Error("failed to read AOC-state snapshot");
      }

      const voices: AocVoiceSnapshot[] = [];
      for (let index = 0; index < 4; index += 1) {
        voices.push(decodeAocVoice(this.module, statePtr + index * AOC_VOICE_STATE_STRUCT_SIZE));
      }

      return {
        voices,
        tickReload: readUint16(this.module, statePtr + AOC_STATE_TICK_RELOAD_OFFSET),
        tickCounter: readUint16(this.module, statePtr + AOC_STATE_TICK_COUNTER_OFFSET),
        filterCutoff: readUint16(this.module, statePtr + AOC_STATE_FILTER_CUTOFF_OFFSET),
        lastSample: readInt16(this.module, statePtr + AOC_STATE_LAST_SAMPLE_BITS_OFFSET),
        filterFQ16: readUint32(this.module, statePtr + AOC_STATE_FILTER_F_Q16_OFFSET),
        filterDampQ16: readUint32(this.module, statePtr + AOC_STATE_FILTER_DAMP_Q16_OFFSET),
        filterLowQ16: readInt32(this.module, statePtr + AOC_STATE_FILTER_LOW_Q16_BITS_OFFSET),
        filterBandQ16: readInt32(this.module, statePtr + AOC_STATE_FILTER_BAND_Q16_BITS_OFFSET),
        modevol: this.module.HEAPU8[statePtr + AOC_STATE_MODEVOL_OFFSET],
        irqEn: this.module.HEAPU8[statePtr + AOC_STATE_IRQ_EN_OFFSET],
        irqIfr: this.module.HEAPU8[statePtr + AOC_STATE_IRQ_IFR_OFFSET],
        filterCtrl: this.module.HEAPU8[statePtr + AOC_STATE_FILTER_CTRL_OFFSET],
        filterRoute: this.module.HEAPU8[statePtr + AOC_STATE_FILTER_ROUTE_OFFSET],
        pitchMode: this.module.HEAPU8[statePtr + AOC_STATE_PITCH_MODE_OFFSET],
        osc3RoLatch: this.module.HEAPU8[statePtr + AOC_STATE_OSC3_RO_LATCH_OFFSET],
        env3RoLatch: this.module.HEAPU8[statePtr + AOC_STATE_ENV3_RO_LATCH_OFFSET],
        batchCount: this.module.HEAPU8[statePtr + AOC_STATE_BATCH_COUNT_OFFSET],
        synthCycleAccum: readUint32(this.module, statePtr + AOC_STATE_SYNTH_CYCLE_ACCUM_OFFSET),
        sampleCount: readBigUint64(this.module, statePtr + AOC_STATE_SAMPLE_COUNT_OFFSET).toString()
      };
    } finally {
      this.module._free(statePtr);
    }
  }

  getTicState(): TicSnapshot {
    const statePtr = this.module._malloc(TIC_STATE_STRUCT_SIZE);
    if (statePtr === 0) {
      throw new Error("failed to allocate TIC-state buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_get_tic_state(this.requireLivePtr(), statePtr);
      if (ok === 0) {
        throw new Error("failed to read TIC-state snapshot");
      }

      return {
        tic1: decodeTicChip(this.module, statePtr),
        tic2: decodeTicChip(this.module, statePtr + TIC_STATE_TIC2_OFFSET),
        pra: this.module.HEAPU8[statePtr + TIC_STATE_PRA_OFFSET],
        prb: this.module.HEAPU8[statePtr + TIC_STATE_PRB_OFFSET],
        ddra: this.module.HEAPU8[statePtr + TIC_STATE_DDRA_OFFSET],
        ddrb: this.module.HEAPU8[statePtr + TIC_STATE_DDRB_OFFSET]
      };
    } finally {
      this.module._free(statePtr);
    }
  }

  getDicState(): DicSnapshot {
    const statePtr = this.module._malloc(DIC_STATE_STRUCT_SIZE);
    if (statePtr === 0) {
      throw new Error("failed to allocate DIC-state buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_get_dic_state(this.requireLivePtr(), statePtr);
      if (ok === 0) {
        throw new Error("failed to read DIC-state snapshot");
      }

      const selectedUnit = this.module.HEAPU8[statePtr] === 1 ? 1 : 0;
      return {
        selectedUnit,
        units: [
          decodeDicUnit(this.module, statePtr + DIC_STATE_UNIT0_OFFSET),
          decodeDicUnit(this.module, statePtr + DIC_STATE_UNIT1_OFFSET)
        ]
      };
    } finally {
      this.module._free(statePtr);
    }
  }

  getCycleCount(): bigint {
    return this.module._am8_web_get_cycle_count(this.requireLivePtr());
  }

  getFrameNo(): number {
    return this.module._am8_web_get_frame_no(this.requireLivePtr());
  }

  isHalted(): boolean {
    return this.module._am8_web_is_halted(this.requireLivePtr()) !== 0;
  }

  pauseDebugger(): void {
    this.module._am8_web_debug_pause(this.requireLivePtr());
  }

  continueDebugger(): void {
    this.module._am8_web_debug_continue(this.requireLivePtr());
  }

  stepDebugger(count = 1): void {
    const stepCount = requirePositiveCount(count, "debugger step count");
    const ok = this.module._am8_web_debug_step(this.requireLivePtr(), BigInt(stepCount));
    if (ok === 0) {
      throw new Error(`failed to start debugger step count ${stepCount}`);
    }
  }

  describeDebuggerRule(spec: DebuggerRuleSpec): DebuggerRuleInfo {
    const everyNth = requirePositiveCount(spec.everyNth, "trace every-nth hit");
    const maxEvents = requirePositiveCount(spec.maxEvents, "trace max events");
    const infoPtr = this.module._malloc(DEBUG_RULE_INFO_STRUCT_SIZE);
    if (infoPtr === 0) {
      throw new Error("failed to allocate debugger rule info buffer in Wasm memory");
    }

    try {
      return this.withRuleStrings(spec.matchText, spec.logText, spec.name, (matchPtr, logPtr, namePtr) => {
        if (
          this.module._am8_web_debug_parse_rule(
            encodeDebugRuleAction(spec.action),
            matchPtr,
            logPtr,
            namePtr,
            everyNth,
            maxEvents,
            infoPtr
          ) === 0
        ) {
          throw new Error("invalid debugger rule");
        }
        return decodeDebuggerRuleInfo(this.module, infoPtr);
      });
    } finally {
      this.module._free(infoPtr);
    }
  }

  addDebuggerRule(spec: DebuggerRuleSpec): void {
    this.describeDebuggerRule(spec);
    const everyNth = requirePositiveCount(spec.everyNth, "trace every-nth hit");
    const maxEvents = requirePositiveCount(spec.maxEvents, "trace max events");
    const ok = this.withRuleStrings(spec.matchText, spec.logText, spec.name, (matchPtr, logPtr, namePtr) =>
      this.module._am8_web_debug_add_rule(
        this.requireLivePtr(),
        encodeDebugRuleAction(spec.action),
        matchPtr,
        logPtr,
        namePtr,
        everyNth,
        maxEvents
      )
    );
    if (ok === 0) {
      throw new Error("failed to add debugger rule");
    }
  }

  removeDebuggerRule(index: number): void {
    const ok = this.module._am8_web_debug_remove_rule_at(this.requireLivePtr(), requireNativeCount(index, "debugger rule index"));
    if (ok === 0) {
      throw new Error("failed to remove debugger rule");
    }
  }

  clearDebuggerRules(): void {
    this.module._am8_web_debug_clear_rules(this.requireLivePtr());
  }

  getDebuggerRuleCount(): number {
    return requireNativeCountAtMost(
      this.module._am8_web_debug_get_rule_count(this.requireLivePtr()),
      BREAKPOINT_CAPACITY,
      "debugger rule count"
    );
  }

  listDebuggerRules(): DebuggerRuleInfo[] {
    const count = this.getDebuggerRuleCount();
    if (count <= 0) {
      return [];
    }

    const dstPtr = this.module._malloc(count * DEBUG_RULE_INFO_STRUCT_SIZE);
    if (dstPtr === 0) {
      throw new Error("failed to allocate debugger rule snapshot buffer in Wasm memory");
    }

    try {
      const copied = requireNativeCountAtMost(
        this.module._am8_web_debug_copy_rules(this.requireLivePtr(), dstPtr, count),
        count,
        "debugger rule copy"
      );
      const rules: DebuggerRuleInfo[] = [];
      for (let index = 0; index < copied; index += 1) {
        rules.push(decodeDebuggerRuleInfo(this.module, dstPtr + index * DEBUG_RULE_INFO_STRUCT_SIZE));
      }
      return rules;
    } finally {
      this.module._free(dstPtr);
    }
  }

  getLastDebuggerRule(): DebuggerRuleInfo | null {
    const infoPtr = this.module._malloc(DEBUG_RULE_INFO_STRUCT_SIZE);
    if (infoPtr === 0) {
      throw new Error("failed to allocate debugger rule info buffer in Wasm memory");
    }

    try {
      if (this.module._am8_web_debug_get_last_rule_info(this.requireLivePtr(), infoPtr) === 0) {
        return null;
      }
      return decodeDebuggerRuleInfo(this.module, infoPtr);
    } finally {
      this.module._free(infoPtr);
    }
  }

  setTraceEnabled(enabled: boolean): void {
    this.module._am8_web_debug_set_trace_enabled(this.requireLivePtr(), enabled ? 1 : 0);
  }

  isTraceEnabled(): boolean {
    return this.module._am8_web_debug_trace_is_enabled(this.requireLivePtr()) !== 0;
  }

  clearTraceLog(): void {
    this.module._am8_web_debug_clear_trace_log(this.requireLivePtr());
  }

  getTraceLogCount(): number {
    return requireNativeCount(this.module._am8_web_debug_get_trace_log_count(this.requireLivePtr()), "trace log count");
  }

  listTraceLogEntries(): TraceLogEntry[] {
    const count = this.getTraceLogCount();
    if (count <= 0) {
      return [];
    }

    const dstPtr = this.module._malloc(count * TRACE_LOG_ENTRY_STRUCT_SIZE);
    if (dstPtr === 0) {
      throw new Error("failed to allocate trace log snapshot buffer in Wasm memory");
    }

    try {
      const copied = requireNativeCountAtMost(
        this.module._am8_web_debug_copy_trace_log(this.requireLivePtr(), dstPtr, count),
        count,
        "trace log copy"
      );
      const entries: TraceLogEntry[] = [];
      for (let index = 0; index < copied; index += 1) {
        entries.push(decodeTraceLogEntry(this.module, dstPtr + index * TRACE_LOG_ENTRY_STRUCT_SIZE));
      }
      return entries;
    } finally {
      this.module._free(dstPtr);
    }
  }

  describeBreakpointExpression(expr: string): BreakpointInfo {
    const infoPtr = this.module._malloc(BREAKPOINT_INFO_STRUCT_SIZE);
    if (infoPtr === 0) {
      throw new Error("failed to allocate breakpoint info buffer in Wasm memory");
    }

    try {
      return this.withAsciiString(expr, (exprPtr) => {
        if (this.module._am8_web_debug_parse_breakpoint_expr(exprPtr, infoPtr) === 0) {
          throw new Error("invalid breakpoint condition");
        }
        return decodeBreakpointInfo(this.module, infoPtr);
      });
    } finally {
      this.module._free(infoPtr);
    }
  }

  addBreakpointExpression(expr: string): void {
    this.describeBreakpointExpression(expr);
    const ok = this.withAsciiString(expr, (exprPtr) =>
      this.module._am8_web_debug_add_breakpoint_expr(this.requireLivePtr(), exprPtr)
    );
    if (ok === 0) {
      throw new Error("failed to add breakpoint condition");
    }
  }

  removeBreakpointExpression(expr: string): void {
    const ok = this.withAsciiString(expr, (exprPtr) =>
      this.module._am8_web_debug_remove_breakpoint_expr(this.requireLivePtr(), exprPtr)
    );
    if (ok === 0) {
      throw new Error("failed to remove breakpoint condition");
    }
  }

  clearBreakpoints(): void {
    this.module._am8_web_debug_clear_breakpoints(this.requireLivePtr());
  }

  getBreakpointCount(): number {
    return requireNativeCountAtMost(
      this.module._am8_web_debug_get_breakpoint_count(this.requireLivePtr()),
      BREAKPOINT_CAPACITY,
      "debugger breakpoint count"
    );
  }

  listBreakpoints(): BreakpointInfo[] {
    const count = this.getBreakpointCount();
    if (count <= 0) {
      return [];
    }

    const dstPtr = this.module._malloc(count * BREAKPOINT_INFO_STRUCT_SIZE);
    if (dstPtr === 0) {
      throw new Error("failed to allocate breakpoint snapshot buffer in Wasm memory");
    }

    try {
      const copied = requireNativeCountAtMost(
        this.module._am8_web_debug_copy_breakpoints(this.requireLivePtr(), dstPtr, count),
        count,
        "debugger breakpoint copy"
      );
      const breakpoints: BreakpointInfo[] = [];
      for (let index = 0; index < copied; index += 1) {
        breakpoints.push(decodeBreakpointInfo(this.module, dstPtr + index * BREAKPOINT_INFO_STRUCT_SIZE));
      }
      return breakpoints;
    } finally {
      this.module._free(dstPtr);
    }
  }

  getLastBreakpoint(): BreakpointInfo | null {
    const infoPtr = this.module._malloc(BREAKPOINT_INFO_STRUCT_SIZE);
    if (infoPtr === 0) {
      throw new Error("failed to allocate breakpoint info buffer in Wasm memory");
    }

    try {
      if (this.module._am8_web_debug_get_last_breakpoint_info(this.requireLivePtr(), infoPtr) === 0) {
        return null;
      }
      return decodeBreakpointInfo(this.module, infoPtr);
    } finally {
      this.module._free(infoPtr);
    }
  }

  getStopReason(): DebugStopReason {
    return decodeDebugStopReason(this.module._am8_web_debug_get_stop_reason(this.requireLivePtr()));
  }

  pushInputByte(value: number): boolean {
    return this.module._am8_web_push_input_byte(this.requireLivePtr(), value & 0xff) !== 0;
  }

  keyEvent(key: Am8Key, pressed: boolean): boolean {
    return this.module._am8_web_key_event(this.requireLivePtr(), key, pressed ? 1 : 0) !== 0;
  }

  drainSerialTx(): Uint8Array {
    const count = requireNativeCountAtMost(
      this.module._am8_web_drain_serial_tx(
        this.requireLivePtr(),
        this.serialScratchPtr,
        SERIAL_SCRATCH_CAPACITY
      ),
      SERIAL_SCRATCH_CAPACITY,
      "serial drain"
    );
    return this.module.HEAPU8.slice(this.serialScratchPtr, this.serialScratchPtr + count);
  }

  getAudioSourceRate(): number {
    return this.module._am8_web_audio_source_rate_hz();
  }

  getAvailableAudioFrames(): number {
    return requireNativeCount(
      this.module._am8_web_audio_available_frames(this.requireLivePtr()),
      "available audio frame count"
    );
  }

  copyAudioFrames(maxFrames = AUDIO_SCRATCH_CAPACITY): Int16Array {
    const frameCount = Math.max(0, Math.min(Math.floor(maxFrames), AUDIO_SCRATCH_CAPACITY));
    if (frameCount === 0) {
      return new Int16Array(0);
    }

    const copied = requireNativeCountAtMost(
      this.module._am8_web_copy_audio_frames(
        this.requireLivePtr(),
        this.audioScratchPtr,
        frameCount
      ),
      frameCount,
      "audio frame copy"
    );
    const frames = new Int16Array(copied);
    frames.set(new Int16Array(this.module.HEAPU8.buffer, this.audioScratchPtr, copied));
    return frames;
  }

  getFramebufferView(): Uint8ClampedArray {
    const framebufferPtr = this.module._am8_web_framebuffer_data(this.requireLivePtr());
    return new Uint8ClampedArray(this.module.HEAPU8.buffer, framebufferPtr, FRAMEBUFFER_SIZE);
  }

  getMemorySize(): number {
    return this.memorySize;
  }

  copyMemory(addr: number, length: number): Uint8Array {
    if (!Number.isFinite(addr) || !Number.isInteger(addr)) {
      throw new Error("memory address must be a finite integer");
    }
    if (!Number.isFinite(length) || !Number.isInteger(length)) {
      throw new Error("memory snapshot length must be a finite integer");
    }

    const address = Math.floor(addr);
    const byteLength = Math.floor(length);
    if (address < 0 || address >= this.memorySize) {
      throw new Error(`memory address must be in range 0x0000..0x${(this.memorySize - 1).toString(16).toUpperCase()}`);
    }
    if (byteLength < 0) {
      throw new Error("memory snapshot length must be >= 0");
    }
    if (address + byteLength > this.memorySize) {
      throw new Error("memory snapshot range exceeds address space");
    }
    if (byteLength === 0) {
      return new Uint8Array(0);
    }

    const dstPtr = this.module._malloc(byteLength);
    if (dstPtr === 0) {
      throw new Error("failed to allocate memory snapshot buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_copy_memory(this.requireLivePtr(), address, dstPtr, byteLength);
      if (ok === 0) {
        throw new Error(`failed to copy memory snapshot at 0x${address.toString(16).toUpperCase()}`);
      }
      return this.module.HEAPU8.slice(dstPtr, dstPtr + byteLength);
    } finally {
      this.module._free(dstPtr);
    }
  }

  writeMemory(addr: number, data: Uint8Array): void {
    if (!Number.isFinite(addr) || !Number.isInteger(addr)) {
      throw new Error("memory address must be a finite integer");
    }

    const address = Math.floor(addr);
    if (address < 0 || address >= this.memorySize) {
      throw new Error(`memory address must be in range 0x0000..0x${(this.memorySize - 1).toString(16).toUpperCase()}`);
    }
    if (address + data.length > this.memorySize) {
      throw new Error("memory write range exceeds address space");
    }
    if (data.length === 0) {
      return;
    }

    const srcPtr = this.module._malloc(data.length);
    if (srcPtr === 0) {
      throw new Error("failed to allocate memory write buffer in Wasm memory");
    }

    try {
      this.module.HEAPU8.set(data, srcPtr);
      const ok = this.module._am8_web_write_memory(this.requireLivePtr(), address, srcPtr, data.length);
      if (ok === 0) {
        throw new Error(`failed to write memory at 0x${address.toString(16).toUpperCase()}`);
      }
    } finally {
      this.module._free(srcPtr);
    }
  }

  detectWorkbenchMode(
    mnemonic: string,
    operand: string | null,
    strictRoundtrip = false
  ): ToolingModeDetection | null {
    const modePtr = this.module._malloc(4);
    const valuePtr = this.module._malloc(8);
    const flagsPtr = this.module._malloc(4);
    if (modePtr === 0 || valuePtr === 0 || flagsPtr === 0) {
      if (modePtr !== 0) this.module._free(modePtr);
      if (valuePtr !== 0) this.module._free(valuePtr);
      if (flagsPtr !== 0) this.module._free(flagsPtr);
      throw new Error("failed to allocate workbench mode scratch buffer in Wasm memory");
    }

    try {
      return this.withAsciiString(mnemonic, (mnemonicPtr) =>
        this.withAsciiString(operand, (operandPtr) => {
          const rc = this.module._am8_web_detect_mode(
            mnemonicPtr,
            operandPtr,
            strictRoundtrip ? 1 : 0,
            modePtr,
            valuePtr,
            flagsPtr
          );
          if (rc === 0) {
            const modeIndex = readUint32(this.module, modePtr);
            if (modeIndex >= TOOLING_MODE_NAMES.length) {
              throw new Error(`unexpected workbench mode index ${modeIndex}`);
            }
            const flags = readUint32(this.module, flagsPtr);
            const valueBig = readBigInt64(this.module, valuePtr);
            if (
              (flags & WORKBENCH_MODE_FLAG_HAS_VALUE) !== 0 &&
              (valueBig > BigInt(Number.MAX_SAFE_INTEGER) || valueBig < BigInt(Number.MIN_SAFE_INTEGER))
            ) {
              throw new Error("workbench mode value exceeds JavaScript safe integer range");
            }
            return {
              mode: TOOLING_MODE_NAMES[modeIndex],
              preferAbs: (flags & WORKBENCH_MODE_FLAG_PREFER_ABS) !== 0,
              value: (flags & WORKBENCH_MODE_FLAG_HAS_VALUE) !== 0 ? Number(valueBig) : null
            };
          }
          if (rc === 2) {
            return null;
          }
          throw new Error(WORKBENCH_MODE_DETECT_ERROR_TEXT[rc] ?? `unexpected workbench mode-detect error ${rc}`);
        })
      );
    } finally {
      this.module._free(modePtr);
      this.module._free(valuePtr);
      this.module._free(flagsPtr);
    }
  }

  encodeWorkbenchInstruction(
    mnemonic: string,
    mode: ToolingModeName,
    pc: number,
    value: number | null
  ): Uint8Array {
    if (!TOOLING_MODE_NAMES.includes(mode)) {
      throw new Error(`unknown workbench mode: ${mode}`);
    }
    const dstPtr = this.module._malloc(3);
    const outSizePtr = this.module._malloc(1);
    if (dstPtr === 0 || outSizePtr === 0) {
      if (dstPtr !== 0) this.module._free(dstPtr);
      if (outSizePtr !== 0) this.module._free(outSizePtr);
      throw new Error("failed to allocate workbench assembler scratch buffer in Wasm memory");
    }

    try {
      return this.withAsciiString(mnemonic, (mnemonicPtr) => {
        const rc = this.module._am8_web_encode_instruction(
          mnemonicPtr,
          TOOLING_MODE_NAMES.indexOf(mode),
          pc,
          value === null ? 0 : 1,
          BigInt(value ?? 0),
          dstPtr,
          3,
          outSizePtr
        );
        if (rc !== 0) {
          throw new Error(WORKBENCH_ASM_ERROR_TEXT[rc] ?? `unexpected workbench assembler error ${rc}`);
        }
        return this.module.HEAPU8.slice(dstPtr, dstPtr + this.module.HEAPU8[outSizePtr]);
      });
    } finally {
      this.module._free(dstPtr);
      this.module._free(outSizePtr);
    }
  }

  evalWorkbenchDirective(
    kind: WorkbenchDirectiveKind,
    operand: string,
    currentPc: number,
    maxOutputBytes: number
  ): DirectiveEvalResult | null {
    const dstPtr = this.module._malloc(maxOutputBytes);
    const writtenPtr = this.module._malloc(4);
    const nextPcPtr = this.module._malloc(8);
    if (dstPtr === 0 || writtenPtr === 0 || nextPcPtr === 0) {
      if (dstPtr !== 0) this.module._free(dstPtr);
      if (writtenPtr !== 0) this.module._free(writtenPtr);
      if (nextPcPtr !== 0) this.module._free(nextPcPtr);
      throw new Error("failed to allocate workbench directive scratch buffer in Wasm memory");
    }

    try {
      return this.withAsciiString(operand, (operandPtr) => {
        const rc = this.module._am8_web_eval_directive(
          kind,
          operandPtr,
          BigInt(currentPc),
          dstPtr,
          maxOutputBytes,
          writtenPtr,
          nextPcPtr
        );
        if (rc === 0) {
          const written = readUint32(this.module, writtenPtr);
          const nextPcBig = readBigInt64(this.module, nextPcPtr);
          if (nextPcBig > BigInt(Number.MAX_SAFE_INTEGER) || nextPcBig < BigInt(Number.MIN_SAFE_INTEGER)) {
            throw new Error("workbench directive next PC exceeds JavaScript safe integer range");
          }
          return {
            bytes: this.module.HEAPU8.slice(dstPtr, dstPtr + written),
            nextPc: Number(nextPcBig)
          };
        }
        if (rc === 2) {
          return null;
        }
        throw new Error(
          WORKBENCH_DIRECTIVE_ERROR_TEXT[rc] ?? `unexpected workbench directive error ${rc}`
        );
      });
    } finally {
      this.module._free(dstPtr);
      this.module._free(writtenPtr);
      this.module._free(nextPcPtr);
    }
  }

  assembleWorkbenchSource(source: string): { base: number; bytes: Uint8Array } {
    const basePtr = this.module._malloc(4);
    const sizePtr = this.module._malloc(4);
    const errorPtrPtr = this.module._malloc(4);
    if (basePtr === 0 || sizePtr === 0 || errorPtrPtr === 0) {
      if (basePtr !== 0) this.module._free(basePtr);
      if (sizePtr !== 0) this.module._free(sizePtr);
      if (errorPtrPtr !== 0) this.module._free(errorPtrPtr);
      throw new Error("failed to allocate workbench source-assembly scratch buffer in Wasm memory");
    }

    try {
      new DataView(this.module.HEAPU8.buffer).setUint32(errorPtrPtr, 0, true);
      return this.withAsciiString(source, (sourcePtr) => {
        const bytesPtr = this.module._am8_web_assemble_source_alloc(
          sourcePtr,
          basePtr,
          sizePtr,
          errorPtrPtr
        );
        const errorPtr = readUint32(this.module, errorPtrPtr);
        if (bytesPtr === 0) {
          try {
            throw new Error(errorPtr === 0 ? "failed to assemble workbench source" : this.readCString(errorPtr));
          } finally {
            if (errorPtr !== 0) {
              this.module._free(errorPtr);
            }
          }
        }

        try {
          const base = readUint32(this.module, basePtr);
          const size = readUint32(this.module, sizePtr);
          return {
            base,
            bytes: this.module.HEAPU8.slice(bytesPtr, bytesPtr + size)
          };
        } finally {
          this.module._free(bytesPtr);
          if (errorPtr !== 0) {
            this.module._free(errorPtr);
          }
        }
      });
    } finally {
      this.module._free(basePtr);
      this.module._free(sizePtr);
      this.module._free(errorPtrPtr);
    }
  }

  disassembleWorkbenchBytes(data: Uint8Array, base: number): string {
    if (!Number.isInteger(base) || base < 0 || base > 0xffff) {
      throw new Error("workbench disassembly base must be within 0x0000..0xFFFF");
    }

    const dataPtr = data.length === 0 ? 0 : this.module._malloc(data.length);
    if (data.length > 0 && dataPtr === 0) {
      throw new Error("failed to allocate workbench disassembly input in Wasm memory");
    }

    try {
      if (dataPtr !== 0) {
        this.module.HEAPU8.set(data, dataPtr);
      }
      const textPtr = this.module._am8_web_disassemble_alloc(dataPtr, data.length, base);
      if (textPtr === 0) {
        throw new Error("failed to disassemble workbench memory range");
      }

      try {
        return this.readCString(textPtr);
      } finally {
        this.module._free(textPtr);
      }
    } finally {
      if (dataPtr !== 0) {
        this.module._free(dataPtr);
      }
    }
  }

  getDiskImageSize(): number {
    return this.diskImageSize;
  }

  isStorageDirty(unit: StorageUnit): boolean {
    return this.module._am8_web_storage_is_dirty(this.requireLivePtr(), unit) !== 0;
  }

  getStorageWriteCounter(unit: StorageUnit): number {
    return this.module._am8_web_storage_write_counter(this.requireLivePtr(), unit) >>> 0;
  }

  mountDiskImage(unit: StorageUnit, image: Uint8Array, readOnly = false): void {
    const webPtr = this.requireLivePtr();
    if (image.length !== this.diskImageSize) {
      throw new Error(`disk image must be exactly ${this.diskImageSize} bytes`);
    }

    const dataPtr = this.module._malloc(image.length);
    if (dataPtr === 0) {
      throw new Error("failed to allocate disk image buffer in Wasm memory");
    }

    try {
      this.module.HEAPU8.set(image, dataPtr);
      const rc = this.module._am8_web_mount_disk_image(
        webPtr,
        unit,
        dataPtr,
        image.length,
        readOnly ? 1 : 0
      );
      if (rc !== 0) {
        throwStorageError(`failed to mount disk ${unit}`, rc);
      }
    } finally {
      this.module._free(dataPtr);
    }
  }

  syncStorage(unit: StorageUnit): void {
    const rc = this.module._am8_web_sync_storage(this.requireLivePtr(), unit);
    if (rc !== 0) {
      throwStorageError(`failed to sync disk ${unit}`, rc);
    }
  }

  ejectStorage(unit: StorageUnit, force = false): void {
    const rc = this.module._am8_web_eject_storage(this.requireLivePtr(), unit, force ? 1 : 0);
    if (rc !== 0) {
      throwStorageError(`failed to eject disk ${unit}`, rc);
    }
  }

  copyDiskImage(unit: StorageUnit): Uint8Array {
    const dstPtr = this.module._malloc(this.diskImageSize);
    if (dstPtr === 0) {
      throw new Error("failed to allocate disk snapshot buffer in Wasm memory");
    }

    try {
      const ok = this.module._am8_web_copy_disk_image(
        this.requireLivePtr(),
        unit,
        dstPtr,
        this.diskImageSize
      );
      if (ok === 0) {
        throw new Error(`failed to copy disk ${unit} image`);
      }
      return this.module.HEAPU8.slice(dstPtr, dstPtr + this.diskImageSize);
    } finally {
      this.module._free(dstPtr);
    }
  }
}
